import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { Svg, Path } from 'react-native-svg';
import { useLanguage } from '@/contexts/LanguageContext';
import NoteAvatar from '@/components/NoteAvatar';

interface RelapseRegisterProps {
  daysSoberBefore: number;
  timestamp: Date;
  isLast?: boolean;
  onMenuPress?: () => void;
}

const RelapseRegister: React.FC<RelapseRegisterProps> = ({ daysSoberBefore: _daysSoberBefore, timestamp, isLast = false, onMenuPress }) => {
  const { t, language } = useLanguage();

  const formatTimestamp = (ts: Date) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
    const noteDate = new Date(ts.getFullYear(), ts.getMonth(), ts.getDate());

    if (noteDate.getTime() === today.getTime()) {
      return t('diary.today');
    } else if (noteDate.getTime() === yesterday.getTime()) {
      return t('diary.yesterday');
    } else {
      const locale = language === 'pt' ? 'pt-BR' : language === 'es' ? 'es-ES' : 'en-US';
      return ts.toLocaleDateString(locale, {
        day: '2-digit',
        month: 'short'
      });
    }
  };

  const formattedTime = formatTimestamp(timestamp);

  return (
    <View style={[styles.container, !isLast && styles.containerWithDivider]}>
      <View style={styles.avatarColumn}>
        <NoteAvatar type="relapse" size={32} />
      </View>

      <View style={styles.userContent}>
        <View style={styles.userHeader}>
          <Text style={styles.authorName}>{t('diary.relapse.title')}</Text>
          <View style={styles.headerRight}>
            <Text style={styles.timestamp}>{formattedTime}</Text>
            <TouchableOpacity style={styles.menuButton} onPress={onMenuPress}>
              <Svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <Path d="M10 5.5C10.8284 5.5 11.5 4.82843 11.5 4C11.5 3.17157 10.8284 2.5 10 2.5C9.17157 2.5 8.5 3.17157 8.5 4C8.5 4.82843 9.17157 5.5 10 5.5Z" fill="#757575"/>
                <Path d="M10 11.5C10.8284 11.5 11.5 10.8284 11.5 10C11.5 9.17157 10.8284 8.5 10 8.5C9.17157 8.5 8.5 9.17157 8.5 10C8.5 10.8284 9.17157 11.5 10 11.5Z" fill="#757575"/>
                <Path d="M10 17.5C10.8284 17.5 11.5 16.8284 11.5 16C11.5 15.1716 10.8284 14.5 10 14.5C9.17157 14.5 8.5 15.1716 8.5 16C8.5 16.8284 9.17157 17.5 10 17.5Z" fill="#757575"/>
              </Svg>
            </TouchableOpacity>
          </View>
        </View>

        <Text style={styles.contentText}>
          {t('diary.relapse.message')}
        </Text>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    paddingHorizontal: 20,
    paddingVertical: 16,
    backgroundColor: '#FFFFFF',
  },
  containerWithDivider: {
    borderBottomWidth: 1,
    borderBottomColor: '#F0EEE4',
  },
  avatarColumn: {
    alignItems: 'center',
    flexShrink: 0,
  },
  userContent: {
    flex: 1,
    flexDirection: 'column',
    gap: 8,
  },
  userHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
  authorName: {
    color: '#151515',
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    lineHeight: 19.6,
    flex: 1,
  },
  headerRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  timestamp: {
    color: '#757C85',
    fontFamily: 'Inter_500Medium',
    fontSize: 12,
    lineHeight: 12,
  },
  menuButton: {
    padding: 4,
  },
  contentText: {
    color: '#151515',
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    lineHeight: 19.6,
  },
});

export default RelapseRegister;
